import React, { useState, useEffect } from 'react';
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Info,
  Sparkles,
  Target,
  ArrowRight,
} from 'lucide-react';

/**
 * Revenue Forecast Panel
 * 
 * Shows projected commission for the next 30 days based on pipeline and benchmarks.
 * Uses deterministic math only - no AI.
 * MA-IMPL-118B: Added campaignId filter support
 */
const RevenueForecastPanel = ({ 
  period = "mtd", 
  vertical, 
  campaignId,
  customStart, 
  customEnd,
  className = "" 
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchForecast = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let url = `/analytics/forecast`;
        const params = new URLSearchParams();
        
        // Build date range from period
        const now = new Date();
        let startDate = new Date();
        
        if (period === "7d") {
          startDate.setDate(now.getDate() - 7);
        } else if (period === "30d") {
          startDate.setDate(now.getDate() - 30);
        } else if (period === "mtd") {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        } else if (period === "ytd") {
          startDate = new Date(now.getFullYear(), 0, 1);
        } else if (period === "custom" && customStart && customEnd) {
          params.set("start_date", customStart);
          params.set("end_date", customEnd);
        }
        
        if (period !== "custom") {
          params.set("start_date", startDate.toISOString().slice(0, 10));
          params.set("end_date", now.toISOString().slice(0, 10));
        }
        
        if (vertical && vertical !== "all") {
          params.set("vertical", vertical);
        }
        
        // MA-IMPL-118B: Include campaign filter
        if (campaignId && campaignId !== "all") {
          params.set("campaign_id", campaignId);
        }
        
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
        
        const res = await api.get(url);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load forecast");
      } finally {
        setLoading(false);
      }
    };
    
    fetchForecast();
  }, [period, vertical, campaignId, customStart, customEnd]);

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return "—";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatPercent = (val) => {
    if (val === null || val === undefined) return "—";
    return `${(val * 100).toFixed(0)}%`;
  };

  if (loading) {
    return (
      <Card className={`glass border-white/10 ${className}`} data-testid="revenue-forecast-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-200">
            <Sparkles className="h-4 w-4 text-amber-400" />
            Projected Commission (Next 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-8 w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`glass border-white/10 border-red-500/20 ${className}`} data-testid="revenue-forecast-panel">
        <CardContent className="pt-6">
          <p className="text-sm text-red-400">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card className={`glass border-white/10 overflow-hidden ${className}`} data-testid="revenue-forecast-panel">
      {/* Gradient accent bar */}
      <div className="h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500" />
      
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-200">
            <Sparkles className="h-4 w-4 text-amber-400" />
            Projected Commission (Next 30 Days)
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-slate-500 hover:text-slate-300" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <div className="text-xs space-y-2">
                  <p className="font-medium">Calculation Method:</p>
                  <p>
                    <strong>From Submitted:</strong> {data.submitted_count} × {formatPercent(data.benchmark_issue_rate)} IR = {data.expected_issued_from_submitted?.toFixed(1)} policies
                  </p>
                  <p>
                    <strong>From Held:</strong> {data.held_not_submitted} × {formatPercent(data.benchmark_conversion)} CR × {formatPercent(data.benchmark_issue_rate)} IR = {data.expected_issued_from_held?.toFixed(1)} policies
                  </p>
                  <p>
                    <strong>Avg Commission:</strong> {formatCurrency(data.avg_commission)} (90-day rolling)
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Main Projection */}
        <div className="text-center py-3">
          <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Expected</div>
          <div className="text-3xl font-bold text-amber-400" data-testid="projected-commission">
            {formatCurrency(data.projected_commission)}
          </div>
        </div>
        
        {/* Best/Conservative Range */}
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
            <div className="text-xs text-slate-400 mb-1 flex items-center justify-center gap-1">
              <TrendingUp className="h-3 w-3 text-emerald-400" />
              Best Case (+15%)
            </div>
            <div className="text-lg font-semibold text-emerald-400">
              {formatCurrency(data.best_case)}
            </div>
          </div>
          <div className="text-center p-2 bg-slate-500/10 rounded-lg border border-slate-500/20">
            <div className="text-xs text-slate-400 mb-1 flex items-center justify-center gap-1">
              <TrendingDown className="h-3 w-3 text-slate-400" />
              Conservative (-15%)
            </div>
            <div className="text-lg font-semibold text-slate-300">
              {formatCurrency(data.conservative_case)}
            </div>
          </div>
        </div>
        
        {/* Pipeline Breakdown */}
        <div className="border-t border-white/10 pt-3">
          <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Pipeline</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Held:</span>
              <span className="text-slate-200 font-medium">{data.held_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Submitted:</span>
              <span className="text-slate-200 font-medium">{data.submitted_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Benchmark CR:</span>
              <span className="text-slate-200 font-medium">{formatPercent(data.benchmark_conversion)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Benchmark IR:</span>
              <span className="text-slate-200 font-medium">{formatPercent(data.benchmark_issue_rate)}</span>
            </div>
          </div>
        </div>
        
        {/* Commission Sources */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-white/5">
          <span>From Submitted: {formatCurrency(data.commission_from_submitted)}</span>
          <span>From Held: {formatCurrency(data.commission_from_held)}</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default RevenueForecastPanel;
